import React from "react";
import { Typography, Box, CircularProgress, IconButton, Tooltip, TextField, Button, MenuItem, Snackbar, Popover } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Fullscreen, FullscreenExit } from "@material-ui/icons";
import { JOINTS, AILMENTS, MARKETS, LOCALES } from "../../api";
import type { Joint, Ailment, Market, PreviewLocale, PreviewAttrs } from "../../api";
import { AutoFillContext, FormValuesContext, FeelContext, ValidationContext, RespondedContext, type FormValues, type FormEntry } from "./ResolvedComponents";
import { previewStyles as styles } from "./previewStyles";

const formatAttrsSummary = (attrs: PreviewAttrs) =>
  `${attrs.joint.replace(/_/g, " ")} · ${attrs.ailment.replace(/_/g, " ")} · ${attrs.market.toUpperCase()} · ${attrs.locale}`;

const attrsEqual = (a: PreviewAttrs, b: PreviewAttrs) =>
  a.joint === b.joint && a.ailment === b.ailment && a.market === b.market && a.locale === b.locale;

export const PatientPreviewShell: React.VFC<{
  isExpanded: boolean;
  onToggleExpand: () => void;
  isLoading: boolean;
  error: unknown;
  attrs: PreviewAttrs;
  onApplyAttrs: (attrs: PreviewAttrs) => void;
  children: React.ReactNode;
}> = ({ isExpanded, onToggleExpand, isLoading, error, attrs, onApplyAttrs, children }) => {
  const [errorOpen, setErrorOpen] = React.useState(false);

  React.useEffect(() => {
    if (error) setErrorOpen(true);
  }, [error]);

  const errorMessage = error instanceof Error ? error.message : "Failed to load preview";

  const [attrsAnchor, setAttrsAnchor] = React.useState<HTMLButtonElement | null>(null);
  const [draftAttrs, setDraftAttrs] = React.useState<PreviewAttrs>(attrs);
  const openAttrs = (e: React.MouseEvent<HTMLButtonElement>) => {
    setDraftAttrs(attrs);
    setAttrsAnchor(e.currentTarget);
  };
  const closeAttrs = () => setAttrsAnchor(null);
  const confirmAttrs = () => {
    onApplyAttrs(draftAttrs);
    setAttrsAnchor(null);
  };

  const [autoFillTrigger, setAutoFillTrigger] = React.useState(0);
  const [clearKey, setClearKey] = React.useState(0);
  const formValuesRef = React.useRef<FormValues>({});
  const [feelContext, setFeelContext] = React.useState<Record<string, unknown>>({});
  const [respondedIds, setRespondedIds] = React.useState<Set<number>>(new Set());
  const requiredIdsRef = React.useRef<Map<number, number | null>>(new Map());
  const [errorIds, setErrorIds] = React.useState<Set<number>>(new Set());

  const formValuesApi = React.useMemo(
    () => ({
      register: (questionId: number, entry: FormEntry) => {
        formValuesRef.current[questionId] = entry;
        const hasValue = entry.value != null && (typeof entry.value === "string" ? entry.value.trim().length > 0 : Array.isArray(entry.value) ? entry.value.length > 0 : true);
        if (hasValue) {
          setErrorIds((prev) => prev.has(questionId) ? (() => { const s = new Set(prev); s.delete(questionId); return s; })() : prev);
        }
        setRespondedIds((prev) => {
          if (hasValue && !prev.has(questionId)) { const s = new Set(prev); s.add(questionId); return s; }
          if (!hasValue && prev.has(questionId)) { const s = new Set(prev); s.delete(questionId); return s; }
          return prev;
        });
        setFeelContext((prev) => {
          const idChanged = entry.feelId && prev[entry.feelId] !== entry.feelValue;
          const newOptionIds = entry.optionFeelIds.filter((optId) => !(optId in prev));
          if (!idChanged && newOptionIds.length === 0) return prev;
          return {
            ...prev,
            ...(idChanged ? { [entry.feelId!]: entry.feelValue } : {}),
            ...Object.fromEntries(newOptionIds.map((optId) => [optId, optId])),
          };
        });
      },
      unregister: (questionId: number) => {
        const entry = formValuesRef.current[questionId];
        delete formValuesRef.current[questionId];
        setRespondedIds((prev) => {
          if (!prev.has(questionId)) return prev;
          const s = new Set(prev);
          s.delete(questionId);
          return s;
        });
        if (entry?.feelId) {
          setFeelContext((prev) => {
            if (!(entry.feelId in prev)) return prev;
            const next = { ...prev };
            delete next[entry.feelId];
            return next;
          });
        }
      },
    }),
    []
  );

  const validationApi = React.useMemo(
    () => ({
      registerRequired: (id: number, pageIndex: number | null) => { requiredIdsRef.current.set(id, pageIndex); },
      unregisterRequired: (id: number) => { requiredIdsRef.current.delete(id); },
    }),
    []
  );

  const validate = React.useCallback((pageIndex?: number | null): boolean => {
    const errors = new Set<number>();
    const isResponseEmpty = (response: unknown): boolean => {
      if (response == null) return true;
      if (Array.isArray(response)) return response.length === 0;
      if (response === "") return true;
      return false;
    };
    for (const [id, page] of requiredIdsRef.current) {
      if (pageIndex != null && page !== pageIndex) continue;
      const entry = formValuesRef.current[id];
      if (!entry || isResponseEmpty(entry.value)) {
        errors.add(id);
      }
    }
    setErrorIds(errors);
    return errors.size === 0;
  }, []);

  const validationContextValue = React.useMemo(
    () => ({ ...validationApi, errorIds, validate }),
    [validationApi, errorIds, validate]
  );

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = Object.values(formValuesRef.current).map(({ questionId, value }) => ({ questionId, value }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "health-form-values.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.previewContainer}>
      <Box display="flex" alignItems="center" px={1} pt={1} style={{ gap: 4 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={openAttrs}
          style={{ fontSize: 11, height: 24, textTransform: "none", padding: "0 8px" }}
        >
          {formatAttrsSummary(attrs)}
        </Button>
        {isLoading && <CircularProgress size={14} />}
      </Box>
      <Popover
        open={attrsAnchor !== null}
        anchorEl={attrsAnchor}
        onClose={closeAttrs}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box p={1.5} display="flex" flexDirection="column" style={{ gap: 8, minWidth: 220 }}>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Joint"
            value={draftAttrs.joint}
            onChange={(e) => setDraftAttrs({ ...draftAttrs, joint: e.target.value as Joint })}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
          >
            {JOINTS.map((j) => (
              <MenuItem key={j} value={j}>{j.replace(/_/g, " ")}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Ailment"
            value={draftAttrs.ailment}
            onChange={(e) => setDraftAttrs({ ...draftAttrs, ailment: e.target.value as Ailment })}
          >
            {AILMENTS.map((a) => (
              <MenuItem key={a} value={a}>{a.replace(/_/g, " ")}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Market"
            value={draftAttrs.market}
            onChange={(e) => setDraftAttrs({ ...draftAttrs, market: e.target.value as Market })}
          >
            {MARKETS.map((m) => (
              <MenuItem key={m} value={m}>{m.toUpperCase()}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Locale"
            value={draftAttrs.locale}
            onChange={(e) => setDraftAttrs({ ...draftAttrs, locale: e.target.value as PreviewLocale })}
          >
            {LOCALES.map((l) => (
              <MenuItem key={l} value={l}>{l}</MenuItem>
            ))}
          </TextField>
          <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
            <Button size="small" onClick={closeAttrs}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={confirmAttrs}
              disabled={attrsEqual(draftAttrs, attrs)}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Popover>
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorOpen(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Box display="flex" alignItems="center" px={1} pt={0.5} style={{ gap: 8 }}>
        <Button size="small" onClick={() => setAutoFillTrigger((n) => n + 1)} style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}>
          Autofill
        </Button>
        <Button size="small" onClick={() => { setClearKey((n) => n + 1); setAutoFillTrigger(0); setRespondedIds(new Set()); }} style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}>
          Clear
        </Button>
        <Button size="small" onClick={handleSubmit} style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}>
          Submit
        </Button>
        <Box style={{ flex: 1 }} />
        <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
          <IconButton size="small" onClick={onToggleExpand}>
            {isExpanded ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && !isLoading && (
        <Box px={2} py={4} textAlign="center">
          <Typography color="error">{errorMessage}</Typography>
        </Box>
      )}
      <RespondedContext.Provider value={respondedIds}>
        <FormValuesContext.Provider value={formValuesApi}>
          <FeelContext.Provider value={feelContext}>
            <ValidationContext.Provider value={validationContextValue}>
              <AutoFillContext.Provider value={autoFillTrigger} key={clearKey}>
                {children}
              </AutoFillContext.Provider>
            </ValidationContext.Provider>
          </FeelContext.Provider>
        </FormValuesContext.Provider>
      </RespondedContext.Provider>
    </div>
  );
};
